<template>
  <div id="app">
    <v-idle @idle="onVidle" :loop="true" :duration="$idleTime" v-show="false" />

    <b-overlay
      z-index="10000"
      fixed
      no-center
      :show="showSignOutWarning"
      rounded="sm"
    >
      <template v-slot:overlay>
        <div
          style="
            position: fixed;
            width: 30rem;
            top: 15rem;
            left: 50%;
            margin-left: -15rem;
            font-size: large;
          "
        >
          <p>
            <b-icon
              variant="warning"
              icon="exclamation-triangle-fill"
              font-scale="3"
            ></b-icon>
          </p>
          <p>
            You will be logged out of MakoSuite in
            <strong>{{ logoutCountdown }} minutes</strong>. <br />Do you wish to
            stay logged in?
          </p>
          <div style="text-align: right; margin-right: 3rem">
            <b-button
              id="idleLogoutBtn"
              variant="outline-primary"
              size="sm"
              @click="signOut"
              >Log Out</b-button
            >
            <b-button
              id="idleContinueBtn"
              variant="primary"
              size="sm"
              style="margin-left: 1rem"
              @click="continueClicked"
              >Continue</b-button
            >
          </div>
        </div>
      </template>
      <Alert />
      <div class="sticky-top" style="background-color: white">
        <b-row>
          <b-col cols="12">
            <Toolbar />
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div id="nav">
              <b-nav tabs>
                <div class="brand">MakoSuite</div>
                <b-nav-item
                  v-if="isFieldUser"
                  id="idSurgicalCasesNav"
                  to="/SurgicalCases"
                  >Surgical Cases</b-nav-item
                >
                <b-nav-item
                  v-if="isFieldUser || isInhouseUser"
                  id="idTreatmentPlanRequestsNav"
                  to="/TreatmentPlanRequests"
                  >Treatment Plan Requests</b-nav-item
                >
                <b-nav-item
                  v-if="isFieldUser"
                  id="idPatientScansNav"
                  to="/PatientScans"
                  >Patient Scans</b-nav-item
                >
                <b-nav-item v-if="isAdminUser" id="idAdminArea" to="/AdminArea"
                  >Admin Area</b-nav-item
                >
              </b-nav>
            </div>
            <b-button
              v-if="isSolidUser"
              variant="primary"
              size="sm"
              class="launch-solid-btn"
              v-bind:href="$solidUrl"
              target="_blank"
              ><b-icon icon="bar-chart-fill"></b-icon> Launch SOLID
              Insights</b-button
            >
          </b-col>
        </b-row>
      </div>
      <div
        v-if="(token && this.$store.state.roles) || $route.path === '/SignIn'"
      >
        <router-view :key="$route.fullPath" />
      </div>
      <div v-else>
        <b-spinner
          variant="warning"
          class="m-5"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import Toolbar from "@/components/Toolbar.vue";
import Alert from "@/components/Alert.vue";
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      refreshIntervalHandler: null,
      countdownIntervalHandler: null,
      token: null,
      showSignOutWarning: false,
      remainingMilliseconds: null,
    };
  },
  components: {
    Toolbar,
    Alert,
  },
  props: [
    "tprhClient",
    "piClient",
    "pspClient",
    "psuClient",
    "scClient",
    "tppClient",
    "buhClient",
    "psvpClient",
  ],
  methods: {
    onVidle() {
      if (
        !this.showSignOutWarning &&
        !this.$store.state.isUploading &&
        this.$route.path !== "/SignIn" &&
        this.$route.path !== "/Callback"
      ) {
        this.startCountdown();
        this.showSignOutWarning = true;
      }
    },
    startCountdown() {
      this.remainingMilliseconds = this.$idleWarningTimeInMilliSec;
      this.countdownIntervalHandler = setInterval(() => {
        this.remainingMilliseconds -= 1000;
      }, 1000);
    },
    continueClicked() {
      clearInterval(this.countdownIntervalHandler);
      this.showSignOutWarning = false;
    },
    signOut() {
      sessionStorage.clear();
      if (this.countdownIntervalHandler) {
        clearInterval(this.countdownIntervalHandler);
      }
      clearInterval(this.$store.state.refreshIntervalHandler);
      this.$router.push({ name: "msLogout" });
    },
    async refreshToken() {
      let result = await this.$msal.acquireToken();
      if (!result) {
        result = await this.$msal.acquireToken();
      }
      // We got a result from MS backend, set new token and roles
      if (result) {
        this.token = result.accessToken;
        this.setRolesAndToken(result.accessToken);
        // Refresh from MS backend didn't work, use existing token if available
      } else if (this.$msal.data.accessToken) {
        this.token = this.$msal.data.accessToken;
        this.setRolesAndToken(this.$msal.data.accessToken);
      }
    },
    onClose(event) {
      event.preventDefault();
    },
    setRolesAndToken(token) {
      // Decode token..
      var base64token = token
        .split(".")[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      var jsonPayload = JSON.parse(
        decodeURIComponent(
          atob(base64token)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
        )
      );
      // .. and set roles in store
      this.$store.commit("SET_ROLES", jsonPayload.roles);
      // Set token for backend calls
      this.$buhClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$tprhClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$piClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$pspClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$scClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$tppClient.authentications["Bearer"].apiKey = "BEARER " + token;
      this.$psvpClient.authentications["Bearer"].apiKey = "BEARER " + token;
      axios.defaults.headers.common["Authorization"] = "BEARER " + token;
      axios.defaults.headers["Content-Type"] = "multipart/form-data";
    },
    startBackendAuthTrigger() {
      // every 15 minutes, we refresh our backend token
      if (!this.$store.state.refreshIntervalHandler) {
        const refreshIntervalHandler = setInterval(async () => {
          await this.refreshToken();
        }, this.$tokenRefreshIntervallInMilliSec);
        this.$store.commit(
          "SET_REFRESH_INTERVAL_HANDLER",
          refreshIntervalHandler
        );
      }
    },
  },
  watch: {
    remainingMilliseconds() {
      if (this.remainingMilliseconds <= 0) {
        this.signOut();
      }
    },
  },
  computed: {
    logoutCountdown() {
      return moment(this.remainingMilliseconds).format("mm:ss");
    },
    isAdminUser() {
      return this.$store.state.roles?.includes("RH.MS.AdminUser");
    },
    isInhouseUser() {
      return this.$store.state.roles?.includes("RH.MS.InhouseUser");
    },
    isFieldUser() {
      return this.$store.state.roles?.includes("RH.MS.FieldUser");
    },
    isSolidUser() {
      return this.$store.state.roles?.includes("RH.MS.SolidUser");
    },
    isIdle() {
      return this.isAppIdle;
    },
  },
  async mounted() {
    var isChrome = !!window.chrome;
    let accepted = null;
    if (isChrome) accepted = true;
    else accepted = false;
    if (!isChrome) {
      accepted = await this.$bvModal.msgBoxOk(
        "The MakoSuite Application works best on Google Chrome. Some features might not work on other Browsers. Please switch to the latest version of Google Chrome.",
        {
          title: `Unsupported browser detected`,
          size: "md",
          buttonSize: "md",
          okVariant: "primary",
          okTitle: "OK",
          cancelVariant: "outline-primary",
          cancelTitle: "Cancel",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
    }
    if (accepted && this.$msal.isAuthenticated()) {
      this.refreshToken();
      this.startBackendAuthTrigger();
    }
    // Load and merge all specs
    let tka = await import(`@/data/${this.$tka}`);
    let tha = await import(`@/data/${this.$tha}`);
    let mckpk = await import(`@/data/${this.$mckpk}`);
    let parameter_spec = {
      ...tka.default,
      ...tha.default,
      ...mckpk.default,
    };
    this.$store.commit("SET_APP_PARAMETER_SPEC", parameter_spec);
  },
};
</script>
<style lang="scss">
@import "~bootstrap/scss/functions", "assets/_stryker.scss";
@import "~bootstrap/scss/bootstrap.scss";
@import "~bootstrap-vue/dist/bootstrap-vue.css";

#app {
  font-family: "Arial", "sans-serif";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  font-size: 0.9rem;
  min-height: 500px;
}
label,
.col-form-label {
  float: left;
  font-size: small;
  color: rgb(95, 95, 95);
}
ul.nav.nav-tabs {
  border-bottom: 4px solid $yellow;
  height: 40px;
  width: 100%;
  background-color: white;
}
.nav-link {
  padding: 0.5rem 1rem;
}

#nav {
  padding: 10px;
  height: 40px;
}

#nav a {
  height: 36px;
  margin-bottom: 1rem;
}

#nav a.router-link-exact-active {
  border-top: 4px solid $yellow;
  background-color: rgb(255, 247, 229);
  font-weight: bold;
}

.brand {
  font-size: x-large;
  line-height: 40px;
  margin: 0 1rem;
}

.jumbotron {
  padding: 1rem 1rem;
  margin-left: 1rem;
  margin-right: 1rem;
  margin-top: 1rem;
}
.btn {
  padding: 0.3rem 1rem;
}
.custom-control {
  position: inherit;
}

h5 {
  margin-bottom: 0;
  font-weight: bold;
}
.spinner-grow {
  width: 5rem;
  height: 5rem;
  margin-top: 3rem;
}

label.required:after {
  color: $red;
  content: " *";
}

.input-group-append {
  height: 31px;
}

body {
  min-width: 768px;
  min-height: 100%;
}

.logo {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.launch-solid-btn {
  float: right;
  margin-right: 1rem;
  margin-top: -2.2rem;
  padding-left: 0.8rem;
  background: #85458a;
  border-color: #85458a;
}

.launch-solid-btn:hover {
  background: #9d6aa1;
  border-color: #9d6aa1;
}
</style>
